import { Badge } from '@components/atoms/Badge';
import { StatusIndicator } from '@components/atoms/StatusIndicator';
import { PlatformStatusComponent } from '@models/platformStatus.model';
import { STATE_PRESENTATION } from '@utils/platformStatus';
import React from 'react';

interface StatusComponentRowProps {
  component: PlatformStatusComponent;
}

/**
 * One service on the platform status screen.
 *
 * Rendered by the super-admin screen's live preview, so whoever is publishing an
 * override sees the resulting row rather than an approximation of it.
 *
 * The dot and service name come from the existing `StatusIndicator` atom. Its
 * `subtitle` slot is deliberately left unused: the atom centres the dot against
 * its whole content, which reads correctly beside a single line but drifts to
 * the middle of the block once a description wraps onto three lines on a phone.
 * The description therefore sits below, indented to line up with the name.
 */
export const StatusComponentRow: React.FC<StatusComponentRowProps> = ({
  component,
}) => {
  const presentation = STATE_PRESENTATION[component.state];

  return (
    <div className="border-b border-gray-100 py-4 last:border-b-0 dark:border-gray-700">
      <div className="flex items-center justify-between gap-3">
        <StatusIndicator
          color={presentation.color}
          label={component.name}
          size="md"
          className="min-w-0"
        />

        <Badge variant={presentation.badgeVariant} size="sm">
          {presentation.label}
        </Badge>
      </div>

      <p className="mt-1 pl-5 text-sm text-gray-500 dark:text-gray-400">
        {component.description}
      </p>

      {component.publicMessage && (
        <p className="mt-2 pl-5 text-sm text-gray-700 dark:text-gray-300">
          {component.publicMessage}
        </p>
      )}
    </div>
  );
};
